/**
 * Shell Header Component
 * Phase 68 M4: 52px fixed header per locked design 001-global-shell.
 * Contains: hamburger (40x40) → logo (28px) → "NEXO" (24px) → spacer → avatar (36px) → popup menu
 * Per L18: NO theme toggle in header (lives in user popup menu per L16)
 */

import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  alpha,
  Avatar,
  Box,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import DarkMode from '@mui/icons-material/DarkMode'
import LightMode from '@mui/icons-material/LightMode'
import Logout from '@mui/icons-material/Logout'
import MenuIcon from '@mui/icons-material/Menu'
import Person from '@mui/icons-material/Person'

import { useAuthContext } from '@contexts/AuthContext'
import { useThemeMode } from '@contexts/ThemeContext'
import logoImage from '@/assets/transparent-logo.png'

interface HeaderProps {
  onMenuClick: () => void
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const navigate = useNavigate()
  const { user, logout } = useAuthContext()
  const { mode, toggleTheme } = useThemeMode()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null)
  const popupOpen = Boolean(anchorEl)

  const initials = user?.username ? user.username[0].toUpperCase() : '?'

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  // BUG-013 fix: Defer navigation until Menu exit animation completes
  // Navigation during exit animation causes MUI to recalculate position
  // with null anchor, falling back to document.body (bottom-right corner)
  const handleProfile = () => {
    setAnchorEl(null)
    setPendingNavigation('/profile')
  }

  // BUG-013 fix: Navigate only after Menu transition fully exits
  const handleTransitionExited = () => {
    if (pendingNavigation) {
      navigate(pendingNavigation)
      setPendingNavigation(null)
    }
  }

  const handleLogout = () => {
    handleClose()
    logout()
  }

  return (
    <Box
      component="header"
      sx={{
        height: 52,
        minHeight: 52,
        display: 'flex',
        alignItems: 'center',
        px: 1, // 8px left padding for hamburger alignment
        pr: 1.5, // 12px right padding
        gap: 0.5, // 4px gap between hamburger and brand
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {/* Hamburger — 40x40px per prototype */}
      <IconButton
        onClick={onMenuClick}
        aria-label="abrir menu"
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1, // 8px
          color: 'text.secondary',
          '&:hover': {
            bgcolor: 'action.hover',
            color: 'text.primary',
          },
        }}
      >
        <MenuIcon sx={{ fontSize: 20 }} />
      </IconButton>

      {/* Logo + Brand — clickable, navigates to Home (L1) */}
      <Box
        role="link"
        tabIndex={0}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75, // 6px
          cursor: 'pointer',
          px: 1, // 8px padding
          py: 0.5, // 4px padding
          borderRadius: 1, // 8px
          transition: 'background 0.15s ease',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
        onClick={() => navigate('/')}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            navigate('/')
          }
        }}
      >
        <Box
          component="img"
          src={logoImage}
          alt="Logotipo NEXO"
          sx={{ width: 28, height: 28, objectFit: 'contain' }}
        />
        <Typography
          sx={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: 24, // 24px per prototype
            letterSpacing: '0.04em',
            color: 'text.primary',
            lineHeight: 1,
            pt: 0.25, // 2px top padding for vertical alignment
          }}
        >
          NEXO
        </Typography>
      </Box>

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Avatar — 36x36px per prototype, opens user popup menu */}
      <Tooltip title={user?.username || 'Usuario'}>
        <IconButton
          onClick={handleAvatarClick}
          aria-label="menu de usuario"
          aria-controls={popupOpen ? 'user-popup-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={popupOpen ? 'true' : undefined}
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            border: '2px solid',
            borderColor: 'divider',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.07),
            color: 'primary.main',
            p: 0,
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
            },
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            {initials}
          </Typography>
        </IconButton>
      </Tooltip>

      {/* User Popup Menu — per L16, contains profile + theme toggle + logout */}
      <Menu
        id="user-popup-menu"
        anchorEl={anchorEl}
        open={popupOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionProps={{ onExited: handleTransitionExited }}
        slotProps={{
          paper: {
            sx: {
              width: 220,
              mt: 1,
              borderRadius: 3, // 12px
              p: 0.75, // 6px
              boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            },
          },
        }}
      >
        {/* Header with user info */}
        <Box sx={{ px: 1.75, py: 1.5, pb: 1.25 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
            {user?.username || 'Usuario'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.25 }}>
            {user?.email || 'correo@nexo.com'}
          </Typography>
        </Box>
        <Divider sx={{ mx: 1, mb: 0.5 }} />

        {/* Mi perfil */}
        <MenuItem
          onClick={handleProfile}
          sx={{ gap: 1.25, px: 1.75, py: 1.125, borderRadius: 1, mx: 0.5, fontSize: 13 }}
        >
          <Person sx={{ fontSize: 16, color: 'text.disabled' }} />
          Mi perfil
        </MenuItem>

        {/* Dark mode toggle — per L16 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1.75,
            py: 1.125,
            mx: 0.5,
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
          onClick={toggleTheme}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            {mode === 'light' ? (
              <DarkMode sx={{ fontSize: 16, color: 'text.disabled' }} />
            ) : (
              <LightMode sx={{ fontSize: 16, color: 'text.disabled' }} />
            )}
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              Modo oscuro
            </Typography>
          </Box>
          <Switch
            size="small"
            checked={mode === 'dark'}
            onChange={toggleTheme}
            onClick={(e) => e.stopPropagation()}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: 'primary.main',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                bgcolor: 'primary.main',
              },
            }}
          />
        </Box>

        <Divider sx={{ mx: 1, my: 0.5 }} />

        {/* Cerrar sesión */}
        <MenuItem
          onClick={handleLogout}
          sx={{
            gap: 1.25,
            px: 1.75,
            py: 1.125,
            borderRadius: 1,
            mx: 0.5,
            fontSize: 13,
            color: 'error.main',
          }}
        >
          <Logout sx={{ fontSize: 16 }} />
          Cerrar sesión
        </MenuItem>
      </Menu>
    </Box>
  )
}

export default Header
